import { useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import knowledgeRqApi, {
  type CategoryItemRow,
  type ExternalContributor,
  type KnowledgeItem,
  type KnowledgeItemRow,
  type ReviewItems,
} from '../api/knowledge-rq.service.api';
import { type QueryOpts } from '../api/query-client';
import { queryKeys } from '../api/query-client-registry';

/**
 * React-query hooks for knowledge items fetched on demand, by id. Rows come from
 * `GET /items?ids=`, a single full item from `GET /items/:id`. Screens that
 * only hold ids (favorites, most viewed, resources, Panda sources) resolve them
 * here instead of joining against the Redux catalog.
 */

export const knowledgeItemsKeys = {
  // `all` derives from the registry so the prefix stays identical to the one the
  // item mutation thunks invalidate.
  all: queryKeys.knowledgeItems,
  detail: (id: string | null | undefined) => [...knowledgeItemsKeys.all, 'detail', id] as const,
  list: (ids: string[]) => [...knowledgeItemsKeys.all, 'list', ids] as const,
  category: (id: string | null | undefined) => [...queryKeys.knowledgeItemsCategory, id] as const,
  review: (ownerIds: string[]) => [...knowledgeItemsKeys.all, 'review', ownerIds] as const,
  contributors: () => [...knowledgeItemsKeys.all, 'contributors'] as const,
};

/**
 * Whether a query error is a 403: the item exists but sits in a workspace this user cannot read.
 *
 * @param error - The query's `error`.
 * @returns `true` for an axios 403.
 * @example
 * if (isForbiddenError(error)) redirectToRestrictedAccessPage({ entity, id });
 */
export const isForbiddenError = (error: unknown) =>
  isAxiosError(error) && error.response?.status === 403;

// A 4xx (bad id, restricted workspace, deleted) is terminal; one retry covers a
// flaky network for everything else.
const retryUnlessClientError = (failureCount: number, error: Error) => {
  const status = isAxiosError(error) ? error.response?.status : undefined;
  return status !== undefined && status >= 400 && status < 500 ? false : failureCount < 1;
};

/**
 * The full item by id (content, keywords, revision log). Disabled without an id; a 403 or
 * 404 is not retried.
 *
 * @param id - The item id, or nothing while the route has none.
 * @param options - Extra `useQuery` options; `enabled` can only narrow.
 * @returns The item query; `data` is the item once it lands.
 * @example
 * const { data: item, error, isPending } = useKnowledgeItem(id);
 */
export function useKnowledgeItem(
  id: string | null | undefined,
  options: QueryOpts<KnowledgeItem> = {},
) {
  return useQuery<KnowledgeItem>({
    queryKey: knowledgeItemsKeys.detail(id),
    queryFn: ({ signal }) => knowledgeRqApi.getItem(id as string, { signal }),
    retry: retryUnlessClientError,
    ...options,
    enabled: Boolean(id) && (options.enabled ?? true),
  });
}

/**
 * Distinct, non-empty ids in first-seen order: the list query's key and request.
 *
 * @param ids - Ids as the call site holds them, holes allowed.
 * @returns The ids to fetch; empty disables the query.
 * @example
 * toIdsKey(['a', null, 'a']); // ['a']
 */
export const toIdsKey = (ids: Array<string | null | undefined>) =>
  Array.from(new Set(ids.filter((id): id is string => Boolean(id))));

/**
 * Rows for a list of ids, in the order asked, as one request per distinct id set.
 * Ids the caller cannot see are missing from `data`. Disabled (no fetch, `data`
 * undefined) when there are no ids.
 *
 * @param ids - Ids as the call site holds them, holes allowed.
 * @param options - Extra `useQuery` options; `enabled` can only narrow.
 * @returns The rows query; `data` is the rows once they land.
 * @example
 * const { data: rows, isPending } = useKnowledgeItems(openEvents.map(event => event.id));
 */
export function useKnowledgeItems(
  ids: Array<string | null | undefined>,
  options: QueryOpts<KnowledgeItemRow[]> = {},
) {
  // React Query hashes the key by value, so a fresh array per render is the same key.
  const idsKey = toIdsKey(ids);
  return useQuery<KnowledgeItemRow[]>({
    queryKey: knowledgeItemsKeys.list(idsKey),
    queryFn: ({ signal }) => knowledgeRqApi.listItems(idsKey, { signal }),
    retry: retryUnlessClientError,
    ...options,
    enabled: idsKey.length > 0 && (options.enabled ?? true),
  });
}

export type KnowledgeItemRowsById = Record<string, KnowledgeItemRow>;

/**
 * `useKnowledgeItems` as a lookup map, for call sites that resolve rows one at
 * a time (resource parsers, favorites, Panda cards).
 *
 * @param ids - Ids as the call site holds them, holes allowed.
 * @param options - Extra `useQuery` options, e.g. `placeholderData: keepPreviousData`.
 * @returns `itemsById` (empty until the rows land), `isLoading` (a first fetch in
 *   flight, never for an empty id list) and `isError`.
 * @example
 * const { itemsById } = useKnowledgeItemsById(cards.map(card => card.documentId));
 * const title = itemsById[card.documentId]?.title;
 */
export function useKnowledgeItemsById(
  ids: Array<string | null | undefined>,
  options: QueryOpts<KnowledgeItemRow[]> = {},
) {
  const { data, isLoading, isError } = useKnowledgeItems(ids, options);
  const itemsById: KnowledgeItemRowsById = Object.fromEntries(
    (data ?? []).map(row => [row.id, row]),
  );
  return { itemsById, isLoading, isError };
}

/**
 * The rows of one binder in binder order, each with `linked`, `isSourceDerived` and, on
 * links, `linkOwnerIds`. Disabled without an id; a 403 or 404 is not retried.
 *
 * @param categoryId - The binder id, or nothing while the route has none.
 * @param options - Extra `useQuery` options; `enabled` can only narrow.
 * @returns The rows query; `data` is the rows once they land.
 * @example
 * const { data: items = [], isPending } = useCategoryItems(binderId);
 */
export function useCategoryItems(
  categoryId: string | null | undefined,
  options: QueryOpts<CategoryItemRow[]> = {},
) {
  return useQuery<CategoryItemRow[]>({
    queryKey: knowledgeItemsKeys.category(categoryId),
    queryFn: ({ signal }) => knowledgeRqApi.listCategoryItems(categoryId as string, { signal }),
    retry: retryUnlessClientError,
    ...options,
    enabled: Boolean(categoryId) && (options.enabled ?? true),
  });
}

/**
 * The home review data: items expired or due for review grouped by owner id (`unassigned`
 * for none, largest group first) and `reviewableCount`, the size of the review cycle.
 *
 * @param ownerIds - Keep only these owners' groups; empty for all.
 * @param options - Extra `useQuery` options.
 * @returns The review query; `data` is `{ groups, reviewableCount }` once it lands.
 * @example
 * const { data: review, isPending } = useReviewItems();
 * const mine = review?.groups[userId] ?? [];
 */
export function useReviewItems(ownerIds: string[] = [], options: QueryOpts<ReviewItems> = {}) {
  const ownersKey = toIdsKey(ownerIds);
  return useQuery<ReviewItems>({
    queryKey: knowledgeItemsKeys.review(ownersKey),
    queryFn: ({ signal }) => knowledgeRqApi.listReviewItems(ownersKey, { signal }),
    ...options,
  });
}

/**
 * The org's external contributors (people named on items who are not users), for the
 * editor's Contributors dropdown.
 *
 * @param options - Extra `useQuery` options.
 * @returns The contributors query; `data` is the list once it lands.
 * @example
 * const { data: externalContributors = [] } = useContributors();
 */
export function useContributors(options: QueryOpts<ExternalContributor[]> = {}) {
  return useQuery<ExternalContributor[]>({
    queryKey: knowledgeItemsKeys.contributors(),
    queryFn: ({ signal }) => knowledgeRqApi.listContributors({ signal }),
    ...options,
  });
}
